import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { WizardPlayer } from '../../../components';
import { useTownAssets } from '../../../stores';

import InviteAckCompanyPage from './InviteAckCompanyPage';
import InviteAckConfirmPage from './InviteAckConfirmPage';
import InviteAckIdentityPage from './InviteAckIdentityPage';
import InviteAckLogoPage from './InviteAckLogoPage';
import InviteAckSuccessPage from './InviteAckSuccessPage';
import InviteAckTeamPage from './InviteAckTeamPage';
import InviteAckTeamSizePage from './InviteAckTeamSizePage';

function InviteAckWizard(props) {
  const {
    invite,
    piid,
    onAccepted,
    onRefused,
    onCompanyConfirmed,
    onClose,
  } = props;
  const { assets } = useTownAssets();
  const { translate } = assets;

  const companyConfirmed = Boolean(invite?.prtnrInvtTrgtCuid);
  const target = invite?.prtnrInvtInfo?.target;

  const pages = useMemo(
    () => {
      const title = translate('partnership');

      return {
        // The invitee names the company first, until it is confirmed
        ...((companyConfirmed)
          ? {}
          : {
              company: {
                title,
                stepLbl: translate('company'),
                Content: InviteAckCompanyPage,
              },
              logo: {
                title,
                stepLbl: translate('logo'),
                Content: InviteAckLogoPage,
              },
            }),
        identity: {
          title,
          stepLbl: translate('identification'),
          Content: InviteAckIdentityPage,
        },
        team: {
          title,
          stepLbl: translate('team'),
          Content: InviteAckTeamPage,
        },
        teamSize: {
          title,
          stepLbl: translate('size'),
          Content: InviteAckTeamSizePage,
        },
        confirm: {
          title,
          stepLbl: translate('summary'),
          Content: InviteAckConfirmPage,
        },
        success: {
          title,
          stepLbl: translate('done'),
          Content: InviteAckSuccessPage,
        },
      };
    },
    [translate, companyConfirmed],
  );

  const defaultPageState = useMemo(
    () => ({
      piid,
      invite,
      cstmrLbl: invite?.prtnrInvtInfo?.target?.cstmrLbl,
      fname: invite?.prtnrInvtInfo?.target?.fname ?? '',
      lname: invite?.prtnrInvtInfo?.target?.lname ?? '',
      email: invite?.prtnrInvtInfo?.target?.email ?? '',
      onAccepted,
      onRefused,
      onCompanyConfirmed,
      // The company the inviter picked stands as it is
      cstmrGovId: (target?.govIdLocked) ? target.govId : undefined,
      rlDesc: '',
      grpLbl: '',
      grpDesc: '',
      // Until the invitee edits either of them, both follow the category
      autoGrpEdit: true,
      teamSetup: 'solo',
    }),
    [invite, piid, target, onAccepted, onRefused, onCompanyConfirmed],
  );

  return (
    <WizardPlayer
      pages={pages}
      initialPage={Object.keys(pages)[0]}
      closeCreate={onClose}
      defaultPageState={defaultPageState}
      showProgressBar
      noTownConfig
      mobileFullScreen
    />
  );
}

InviteAckWizard.propTypes = {
  invite: PropTypes.shape({
    prtnrInvtTrgtCuid: PropTypes.string,
    prtnrInvtInfo: PropTypes.shape({
      target: PropTypes.shape({
        govIdLocked: PropTypes.bool,
        govId: PropTypes.string,
        cstmrLbl: PropTypes.string,
        fname: PropTypes.string,
        lname: PropTypes.string,
        email: PropTypes.string,
      }),
    }),
  }).isRequired,
  piid: PropTypes.string.isRequired,
  onAccepted: PropTypes.func.isRequired,
  onRefused: PropTypes.func.isRequired,
  // Answers true when the invitee is sent to another portal
  onCompanyConfirmed: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default InviteAckWizard;
